import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'pt', label: 'PT' }
];

const MENU_ANIMATION = {
  initial: { opacity: 0, y: -10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.2 }
};

const LanguageMenu = ({ mobile, language, onSelect }) => (
  <motion.div
    {...MENU_ANIMATION}
    className={`absolute top-full mt-2 bg-white shadow-lg border border-black/10 ${
      mobile ? 'left-0 min-w-[70px]' : 'right-0 min-w-[80px]'
    }`}
    data-testid={mobile ? 'lang-dropdown-menu-mobile' : 'lang-dropdown-menu'}
  >
    {LANGUAGES.map((lang) => (
      <button
        key={lang.code}
        onClick={() => onSelect(lang.code)}
        className={`w-full text-left px-4 py-2 uppercase tracking-[0.1em] text-xs font-medium transition-colors duration-200 ${
          language === lang.code
            ? 'bg-[#D4C5B0]/20 text-black'
            : 'text-black/60 hover:bg-black/5 hover:text-black'
        }`}
        data-testid={mobile ? `lang-mobile-${lang.code}` : `lang-${lang.code}`}
      >
        {lang.label}
      </button>
    ))}
  </motion.div>
);

const LanguageDropdown = ({ solid, mobile = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const ref = useRef(null);

  const currentLang = LANGUAGES.find(l => l.code === language);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectLanguage = (code) => {
    setLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${mobile ? 'md:hidden' : 'hidden md:block'}`} ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1 uppercase text-xs font-medium transition-all duration-300 ${
          mobile ? 'tracking-[0.1em]' : 'tracking-[0.15em] px-2 py-1'
        } ${solid ? 'text-black' : 'text-white'}`}
        data-testid={mobile ? 'lang-dropdown-toggle-mobile' : 'lang-dropdown-toggle'}
      >
        {currentLang?.label}
        <ChevronDown size={14} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <LanguageMenu mobile={mobile} language={language} onSelect={selectLanguage} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageDropdown;
